import { uploadArchive } from '@/core/domain/archive/Archive/Archive.service';
import { SubDirectoryEnum } from '@/core/domain/archive/Archive/Archive.types';
import {
  useAuthorizeWorkOrderComplement,
  useVerifyAuthorizeWorkOrderComplement,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import { useDisclosure, useWindowSize } from '@/hooks';
import { Alert, Button, Modal, Space, Tag, Typography } from 'antd';
import React, { useState } from 'react';
import notificationError from '@/utils/functions/notificationError';
import { Smartphone } from 'react-feather';
import LGPDTermModal from '../../Complements/ComplementForm/LGPDTermModal';
import './complementAuthorizeModal.less';

import ReactSignatureCanvas from 'react-signature-canvas';
import PortoIsValidCustomer from '@/components/_common/PortoIsValidCustomer';
import { isAppPortocar } from '@/index';
import ComplementVerifyPartIntegration from './ComplementVerifyPecaAi';
import WorkOrderVerifyPolicy, {
  useWorkOrderVerifyHasPolicy,
} from '../../WorkOrderVerifyPolicy';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';

interface Props {
  open: boolean;
  complementId: number;
  workorderId: number;
  isAlerted?: boolean;
  onCancel: () => void;
  workorderNumber: string;
  customerId?: number;
  hasSignature?: boolean;
  hasPecaAiBasket?: boolean;
  hasWomBasket?: boolean;
  setLgpdAlert?(value: boolean): void;
  hasPecaAiOrWomBasket?: boolean;
}

const ComplementAuthorizeModal: React.FC<Props> = ({
  open,
  isAlerted,
  complementId,
  workorderId,
  onCancel,
  workorderNumber,
  customerId,
  hasSignature,
  hasPecaAiBasket,
  hasWomBasket,
  setLgpdAlert,
}) => {
  const [sigPad, setSigPad] = useState<ReactSignatureCanvas | null>(null);
  const [loadingSignature, setLoadingSignature] = useState(false);
  const [viewSignature, setViewSignature] = useDisclosure(false);
  const [lgpdModal, { open: openLgpdModal, close: closeLgpdModal }] =
    useDisclosure(false);
  const { width } = useWindowSize();

  const authorizeComplement = useAuthorizeWorkOrderComplement();
  const verifyAuthorizeComplement = useVerifyAuthorizeWorkOrderComplement();

  const isLoading =
    authorizeComplement.isLoading || verifyAuthorizeComplement.isLoading;

  const workOrderHasPolicy = useWorkOrderVerifyHasPolicy(
    {
      complementId: complementId,
      workOrderId: workorderId,
    },
    open,
  );

  const handleSigPadReturn = async () => {
    if (!sigPad?.isEmpty() && sigPad?.getTrimmedCanvas()) {
      const signature = sigPad?.getTrimmedCanvas().toDataURL('image/png');
      const dataType = signature?.match(/^data:(.+);base64/)?.[1];
      const extension = dataType?.split('/').at(-1)?.toLowerCase();
      const response = await fetch(signature);
      const blob = await response.blob();
      const file = new File(
        [blob],
        `${new Date().toISOString()}.${extension}`,
        {
          type: dataType,
        },
      );
      return {
        signature,
        dataType,
        extension,
        response,
        blob,
        file,
      };
    } else {
      return undefined;
    }
  };

  const handleCreateSignature = async (file?: File, extension?: string) => {
    if (file && extension) {
      setLoadingSignature(true);
      try {
        const upload = await uploadArchive({
          file: [file],
          isPublic: false,
          extension: extension,
          subdirectory: SubDirectoryEnum.Signatures,
        });

        return upload.filePath;
      } catch (error) {
        notificationError(error);
      }
      setLoadingSignature(false);
    }
  };

  async function authorizeOS() {
    try {
      let authorizedSignature: string | undefined;

      if (viewSignature) {
        const sigPad = await handleSigPadReturn();
        authorizedSignature = await handleCreateSignature(
          sigPad?.file,
          sigPad?.extension,
        );
      }

      if (workorderId && complementId) {
        if (hasPecaAiBasket || hasWomBasket) {
          await verifyAuthorizeComplement.mutateAsync({
            workorderId,
            complementId,
          });
        }

        await authorizeComplement.mutateAsync({
          workorderId,
          complementId,
          authorizedSignature,
        });
      }

      handleCancel();
    } catch {}
  }

  const handleCancel = () => {
    onCancel();
    setViewSignature.close();
  };

  return (
    <>
      <Modal
        title="Autorização da ordem de seviço"
        open={open}
        onOk={isAlerted ? openLgpdModal : authorizeOS}
        okText="Autorizar"
        onCancel={() => handleCancel()}
        width={viewSignature ? 1000 : undefined}
        confirmLoading={isLoading}
        style={{ position: 'relative' }}
      >
        {workOrderHasPolicy?.isLoading && (
          <ModalLoadingOverlay messages={['Verificando apólice do veículo']} />
        )}

        {open && (
          <ComplementVerifyPartIntegration
            complementId={complementId}
            workOrderId={workorderId}
            onClose={handleCancel}
          />
        )}

        <Space
          style={{
            width: '100%',
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'center',
          }}
          direction="vertical"
        >
          <p>
            Deseja realmente autorizar a ordem de serviço "
            <strong>{workorderNumber}</strong>"?
          </p>

          {viewSignature && (
            <>
              {width < 576 && (
                <Tag
                  color="yellow"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Smartphone size={16} style={{ marginRight: 10 }} /> Vire a
                  tela para melhor visualização
                </Tag>
              )}

              <ReactSignatureCanvas
                canvasProps={{
                  className: 'authorize-signature hidden-print',
                }}
                ref={(ref) => setSigPad(ref)}
              />

              <Space
                style={{
                  width: '100%',
                  justifyContent: 'space-between',
                }}
                className="hidden-print"
              >
                <Typography.Link strong onClick={setViewSignature.close}>
                  Cancelar
                </Typography.Link>

                <Typography.Link
                  strong
                  style={{ color: 'red' }}
                  onClick={() => {
                    sigPad?.clear();
                    handleCreateSignature(undefined);
                  }}
                >
                  Limpar
                </Typography.Link>
              </Space>
            </>
          )}

          {!hasSignature && (
            <>
              {!viewSignature && (
                <Button
                  type="link"
                  className="hidden-print"
                  onClick={setViewSignature.open}
                  loading={loadingSignature}
                >
                  Assinar
                </Button>
              )}

              <div style={{ marginTop: 0 }}>Assinatura digital do cliente</div>
            </>
          )}

          {hasPecaAiBasket && (
            <Alert
              type="warning"
              message={<strong>Atenção</strong>}
              description={
                <>
                  <p>
                    Ao autorizar este orçamento, será gerado automaticamente um
                    pedido no <strong>Peça Aí</strong> para as peças incluídas.
                  </p>
                  <p style={{ marginBottom: 0 }}>
                    Após a autorização, o acompanhamento do pedido deverá ser
                    feito diretamente na plataforma do <strong>Peça Aí</strong>.
                  </p>
                </>
              }
            />
          )}

          {isAppPortocar && (
            <PortoIsValidCustomer
              description="Para fazer o fechamento é obrigatório que os dados do cliente estejam completos"
              customerId={customerId}
            />
          )}
        </Space>
      </Modal>

      {customerId && (
        <LGPDTermModal
          customerId={customerId}
          open={lgpdModal}
          onClose={closeLgpdModal}
          onCallback={authorizeOS}
          setAlert={setLgpdAlert}
        />
      )}

      <WorkOrderVerifyPolicy
        complement={workOrderHasPolicy?.complement}
        refetch={workOrderHasPolicy?.refetch}
        workOrder={workOrderHasPolicy?.workOrder}
        onCancel={handleCancel}
      />
    </>
  );
};

export default ComplementAuthorizeModal;
